import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, CardMedia, IconButton, Tooltip } from '@mui/material';
import { Delete, PlayCircleOutline, RemoveRedEye, PlaylistAdd, PlaylistPlay } from '@mui/icons-material'; // Importation des icônes
import { doc, getDoc, updateDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';

// Conteneur global pour la liste des K-Dramas
const MyListContainer = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '2rem',
    backgroundColor: '#141414',
    minHeight: '100vh',
};

// Carte K-Drama avec effet de hover
const KdramaCard = {
    backgroundColor: '#1e1e1e',
    borderRadius: '15px',
    color: 'white',
    margin: '1rem',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
        transform: 'scale(1.05)',
    },
};

const KdramaCardMedia = {
    height: 300,
    borderRadius: '15px 15px 0 0',
};

// Titre section
const SectionTitle = {
    color: '#fff',
    marginBottom: '1rem',
    textAlign: 'center',
    fontFamily: 'SUSE, sans-serif',
    fontWeight: 'bold',
};

// Style des boutons
const actionButtonStyles = {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '1rem',
    gap: '0.5rem',
};
const imageBaseURL = 'https://image.tmdb.org/t/p/w500';
function MyListComponent() {
    const { id } = useParams();
    const [watchedKdramas, setWatchedKdramas] = useState([]);
    const [inProgressKdramas, setInProgressKdramas] = useState([]);
    const [toWatchKdramas, setToWatchKdramas] = useState([]);
    const [favoriteKdramas, setFavoriteKdramas] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const user = auth.currentUser;
        if (user) {
            const fetchUserKdramas = async () => {
                const userDocRef = doc(db, 'users', user.uid);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    setWatchedKdramas(userData.watchedKdramas || []);
                    setInProgressKdramas(userData.inProgressKdramas || []);
                    setToWatchKdramas(userData.toWatchKdramas || []);
                    setFavoriteKdramas(userData.favoriteKdramas || []);
                    setLoading(false);
                }
            };
            fetchUserKdramas();
        }
    }, [id]);

    // Suppression du K-Drama d'une liste
    const removeFromList = async (kdrama, listName) => {
        const user = auth.currentUser;
        if (!user) return;

        const userRef = doc(db, 'users', user.uid);

        try {
            // Mise à jour de la liste en supprimant le K-Drama
            await updateDoc(userRef, {
                [listName]: arrayRemove(kdrama),
            });

            // Mise à jour locale de l'état
            if (listName === 'watchedKdramas') {
                setWatchedKdramas(prev => prev.filter(d => d.id !== kdrama.id));
            } else if (listName === 'inProgressKdramas') {
                setInProgressKdramas(prev => prev.filter(d => d.id !== kdrama.id));
            } else if (listName === 'toWatchKdramas') {
                setToWatchKdramas(prev => prev.filter(d => d.id !== kdrama.id));
            } else if (listName === 'favoriteKdramas') {
                setFavoriteKdramas(prev => prev.filter(d => d.id !== kdrama.id));
            }
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
        }
    };

    // Déplacer un K-Drama d'une liste à une autre
    const moveKdramaToList = async (kdrama, fromList, toList) => {
        const user = auth.currentUser;
        if (!user) return;

        const userRef = doc(db, 'users', user.uid);

        try {
            // Supprimer du fromList et ajouter à toList
            await updateDoc(userRef, {
                [fromList]: arrayRemove(kdrama),
                [toList]: arrayUnion(kdrama),
            });

            // Mettre à jour les états locaux pour les listes
            if (fromList === 'watchedKdramas') {
                setWatchedKdramas(prev => prev.filter(d => d.id !== kdrama.id));
            } else if (fromList === 'inProgressKdramas') {
                setInProgressKdramas(prev => prev.filter(d => d.id !== kdrama.id));
            } else if (fromList === 'toWatchKdramas') {
                setToWatchKdramas(prev => prev.filter(d => d.id !== kdrama.id));
            } else if (fromList === 'favoriteKdramas') {
                setFavoriteKdramas(prev => prev.filter(d => d.id !== kdrama.id));
            }


            if (toList === 'watchedKdramas') {
                setWatchedKdramas(prev => [...prev, kdrama]);
            } else if (toList === 'inProgressKdramas') {
                setInProgressKdramas(prev => [...prev, kdrama]);
            } else if (toList === 'toWatchKdramas') {
                setToWatchKdramas(prev => [...prev, kdrama]);
            } else if (toList === 'favoriteKdramas') {
                setFavoriteKdramas(prev => [...prev, kdrama]);
            }
        } catch (error) {
            console.error('Erreur lors du déplacement:', error);
        }
    };

    const navigateToKdramaDetail = (id) => {
        navigate(`/kdrama/${id}`);
    };
    const formatPosterUrl = (url) => {
        return url.startsWith('http') ? url : `${imageBaseURL}${url}`;
    };
    if (loading) {
        return <LoadingSpinner size={60} color="secondary" text="Chargement des données..." />;
    }

    return (
        <Box sx={MyListContainer}>

            <Typography variant="h4" sx={SectionTitle}>
                K-Dramas favoris
            </Typography>
            <Grid container spacing={3} justifyContent="center">
                {favoriteKdramas.length === 0 ? (
                    <Typography sx={{ color: '#aaa' }}>Vous n'avez aucun K-Drama dans cette catégorie.</Typography>
                ) : (
                    favoriteKdramas.map((kdrama) => (
                        <Grid item key={kdrama.id} xs={12} sm={6} md={4} lg={3}>
                            <Card sx={KdramaCard}>
                                <CardMedia
                                    component="img"
                                    image={formatPosterUrl(kdrama.posterUrl || 'default-poster.png')}
                                    alt={kdrama.title}
                                    sx={KdramaCardMedia}
                                />
                                <CardContent>
                                    <Typography variant="h6">{kdrama.title}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {kdrama.year}
                                    </Typography>
                                    <Box sx={actionButtonStyles}>
                                        <Tooltip title="Voir les details">
                                            <IconButton color="primary" onClick={() => navigateToKdramaDetail(kdrama.id)}>
                                                <RemoveRedEye />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer des favoris">
                                            <IconButton color="error" onClick={() => removeFromList(kdrama, 'favoriteKdramas')}>
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>

                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>


            {/* Section des K-Dramas Vus */}
            <Typography variant="h4" sx={SectionTitle}>
                K-Dramas Vus
            </Typography>
            <Grid container spacing={3} justifyContent="center">
                {watchedKdramas.length === 0 ? (
                    <Typography sx={{ color: '#aaa' }}>Vous n'avez aucun K-Drama dans cette catégorie.</Typography>
                ) : (
                    watchedKdramas.map((kdrama) => (
                        <Grid item key={kdrama.id} xs={12} sm={6} md={4} lg={3}>
                            <Card sx={KdramaCard}>
                                <CardMedia
                                    component="img"
                                    image={formatPosterUrl(kdrama.posterUrl || 'default-poster.png')}
                                    alt={kdrama.title}
                                    sx={KdramaCardMedia}
                                />
                                <CardContent>
                                    <Typography variant="h6">{kdrama.title}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {kdrama.year}
                                    </Typography>
                                    <Box sx={actionButtonStyles}>
                                        <Tooltip title="Voir les details">
                                            <IconButton color="primary" onClick={() => navigateToKdramaDetail(kdrama.id)}>
                                                <RemoveRedEye />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer de la liste">
                                            <IconButton color="error" onClick={() => removeFromList(kdrama, 'watchedKdramas')}>
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Ajouter en cours">
                                            <IconButton color="secondary" onClick={() => moveKdramaToList(kdrama, 'watchedKdramas', 'inProgressKdramas')}>
                                                <PlaylistPlay />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>

            {/* Section des K-Dramas en cours */}
            <Typography variant="h4" sx={SectionTitle} style={{ marginTop: '2rem' }}>
                K-Dramas en Cours
            </Typography>
            <Grid container spacing={3} justifyContent="center">
                {inProgressKdramas.length === 0 ? (
                    <Typography sx={{ color: '#aaa' }}>Vous n'avez aucun K-Drama dans cette catégorie.</Typography>
                ) : (
                    inProgressKdramas.map((kdrama) => (
                        <Grid item key={kdrama.id} xs={12} sm={6} md={4} lg={3}>
                            <Card sx={KdramaCard}>
                                <CardMedia
                                    component="img"
                                    image={formatPosterUrl(kdrama.posterUrl || 'default-poster.png')}
                                    alt={kdrama.title}
                                    sx={KdramaCardMedia}
                                />
                                <CardContent>
                                    <Typography variant="h6">{kdrama.title}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {kdrama.year}
                                    </Typography>
                                    <Box sx={actionButtonStyles}>
                                        <Tooltip title="Voir les details">
                                            <IconButton color="primary" onClick={() => navigateToKdramaDetail(kdrama.id)}>
                                                <RemoveRedEye />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer de la liste">
                                            <IconButton color="error" onClick={() => removeFromList(kdrama, 'inProgressKdramas')}>
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Ajouter à la liste des k-dramas vus">
                                            <IconButton color="secondary" onClick={() => moveKdramaToList(kdrama, 'inProgressKdramas', 'watchedKdramas')}>
                                                <PlaylistAdd />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>

            {/* Section des K-Dramas à voir */}
            <Typography variant="h4" sx={SectionTitle} style={{ marginTop: '2rem' }}>
                K-Dramas à Voir
            </Typography>
            <Grid container spacing={3} justifyContent="center">
                {toWatchKdramas.length === 0 ? (
                    <Typography sx={{ color: '#aaa' }}>Vous n'avez aucun K-Drama dans cette catégorie.</Typography>
                ) : (
                    toWatchKdramas.map((kdrama) => (
                        <Grid item key={kdrama.id} xs={12} sm={6} md={4} lg={3}>
                            <Card sx={KdramaCard}>
                                <CardMedia
                                    component="img"
                                    image={formatPosterUrl(kdrama.posterUrl || 'default-poster.png')}
                                    alt={kdrama.title}
                                    sx={KdramaCardMedia}
                                />
                                <CardContent>
                                    <Typography variant="h6">{kdrama.title}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {kdrama.year}
                                    </Typography>
                                    <Box sx={actionButtonStyles}>
                                        <Tooltip title="Voir les details">
                                            <IconButton color="primary" onClick={() => navigateToKdramaDetail(kdrama.id)}>
                                                <RemoveRedEye />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer de la liste">
                                            <IconButton color="error" onClick={() => removeFromList(kdrama, 'toWatchKdramas')}>
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Ajouter en cours">
                                            <IconButton color="secondary" onClick={() => moveKdramaToList(kdrama, 'toWatchKdramas', 'inProgressKdramas')}>
                                                <PlayCircleOutline />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>
        </Box>
    );
}

export default MyListComponent;
